import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Sparkles,
  Clock,
  Filter,
  CreditCard,
  CheckCircle2,
  Briefcase,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import { useInstallments, useMySubscription } from '../../application/hooks/useSubscription';
import { useTranslation } from '../../application/hooks/useTranslation';
import { formatDate } from '../../domain/formatters/date';
import { InstallmentStatus, DelinquencyStatus } from '../../domain/enums/SystemEnums';
import {
  TENOR,
  calculateAmountDueNow,
  getInstallmentStatusSpec,
  getValidInstallmentRows,
  isInstallmentCleared,
  resolveEffectiveInstallmentStatus,
  type GridSlot,
} from '../../domain/calculators/installments';
import { QueryState } from '../components/feedback/QueryState';
import { AmountDisplay } from '../components/ui/AmountDisplay';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import InstallmentGrid from '../components/installments/InstallmentGrid';
import './pages.css';
import '../components/installments/installments.css';
import { BKASH_ENABLED, PAY_FALLBACK_PATH } from '../../config/features';
import { PayLink } from '../components/payments/PayLink';

const FILTERS: Array<InstallmentStatus | 'ALL'> = [
  'ALL',
  InstallmentStatus.UPCOMING,
  InstallmentStatus.DUE,
  InstallmentStatus.PAID,
  InstallmentStatus.ADVANCED,
  InstallmentStatus.IN_NOTICE_PERIOD,
  InstallmentStatus.DEFAULTED,
];

/**
 * Solidified 120-Month Installment Ledger & Matrix (DEVELOPMENT_PLAN.md §6.3).
 * Denominator is always 120; BD-20 Amount due now formula; unified status table.
 */
export default function InstallmentsPage() {
  const { t, locale } = useTranslation();
  const sub = useMySubscription();
  const rows = useInstallments(sub.data?.id);
  const [status, setStatus] = useState<InstallmentStatus | 'ALL'>('ALL');
  const [active, setActive] = useState<GridSlot | null>(null);

  const validRows = useMemo(() => getValidInstallmentRows(rows.data), [rows.data]);

  const filtered = useMemo(() => {
    if (status === 'ALL') return validRows;
    return validRows.filter((r) => {
      const effective = resolveEffectiveInstallmentStatus(r.status, r.dueDate);
      return effective === status;
    });
  }, [validRows, status]);

  const isClosed = sub.data?.delinquencyStatus === DelinquencyStatus.CLOSED;
  const isInactive =
    sub.data?.delinquencyStatus === DelinquencyStatus.INACTIVE ||
    sub.data?.delinquencyStatus === DelinquencyStatus.DISABLED_DEFAULT;
  const isNotice = sub.data?.delinquencyStatus === DelinquencyStatus.IN_NOTICE_PERIOD;

  // Solidified calculation metrics per §6.3
  const rowClearedCount = useMemo(
    () => validRows.filter((r) => isInstallmentCleared(r.status)).length,
    [validRows],
  );
  const clearedCount = sub.data?.clearedInstallments ?? rowClearedCount;
  const progressPercent = Math.min(100, Math.round((clearedCount / TENOR) * 100));

  const amountDueResult = useMemo(
    () => calculateAmountDueNow(rows.data, sub.data?.advanceBalance),
    [rows.data, sub.data?.advanceBalance],
  );
  const amountDueNow = amountDueResult.amountDueNow;

  // Next unsettled row for secondary display: ordered by installmentNumber (1..120)
  const nextUnsettledRow = useMemo(() => {
    return validRows.find((r) => !isInstallmentCleared(r.status));
  }, [validRows]);

  // Pre-verification detection per §6.3 Q3 & §3.5
  // When rows query has an error, it is NOT "Schedule Not Generated", it is a fetch error!
  const isScheduleEmpty =
    !rows.isPending &&
    !rows.isError &&
    Boolean(sub.data) &&
    (sub.data?.totalInstallments === 0 || !rows.data || rows.data.length === 0);

  return (
    <div data-testid="installments-page">
      {/* 1. Page Title */}
      <div className="page-head">
        <h1 className="page-title">{t('installments.title')}</h1>
      </div>

      {sub.isNoSubscription ? (
        <Card data-testid="agent-no-subscription-state" className="inst-empty-agent-card">
          <div style={{ padding: '32px 16px', textAlign: 'center', maxWidth: '540px', margin: '0 auto' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '14px',
                borderRadius: '50%',
                background: '#f0fdf4',
                color: '#16a34a',
                marginBottom: '14px',
              }}
            >
              <Briefcase size={32} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--color-text, #0f172a)' }}>
              {t('installments.agentNoSubscriptionTitle')}
            </h2>
            <p className="muted" style={{ fontSize: '0.875rem', lineHeight: 1.6, margin: '0 0 24px 0' }}>
              {t('installments.agentNoSubscriptionDesc')}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Link to="/team">
                <Button variant="primary">
                  {t('installments.viewTeam')} →
                </Button>
              </Link>
              <Link to="/commissions">
                <Button variant="secondary">
                  {t('installments.viewCommissions')} →
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <QueryState
            loading={sub.isPending}
            error={sub.error}
            onRetry={() => void sub.refetch()}
            skeletonCount={2}
          />

          {sub.data && (
            <>
              {/* Account / Subscription Status Banner (Q11 / §3.6) */}
              {isClosed && (
                <div
                  className="inst-status-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    marginBottom: 16,
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                  }}
                >
                  <Lock size={20} />
                  <div>
                    <strong>{t('installments.closedBannerTitle')}</strong>: {t('installments.closedBannerDesc')}
                  </div>
                </div>
              )}

              {isInactive && !isClosed && (
                <div
                  className="inst-status-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    marginBottom: 16,
                    background: '#fef2f2',
                    border: '1px solid #f87171',
                    color: '#b91c1c',
                  }}
                >
                  <AlertTriangle size={20} />
                  <div>
                    <strong>{t('installments.inactiveBannerTitle')}</strong>: {t('installments.inactiveBannerDesc')}
                  </div>
                </div>
              )}

              {isNotice && (
                <div
                  className="inst-status-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    marginBottom: 16,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                  }}
                >
                  <AlertTriangle size={20} />
                  <div>
                    <strong>{t('installments.noticeBannerTitle')}</strong>: {t('installments.noticeBannerDesc')}
                  </div>
                </div>
              )}

              {/* 2. 4-KPI Summary Deck */}
              <div className="inst-kpi-grid">
                {/* KPI 1: Cleared Installments */}
                <Card data-testid="cleared-ring">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <CheckCircle2 size={14} />
                          </span>
                          {t('installments.cleared')}
                        </span>
                        <Badge tone="success">{progressPercent}%</Badge>
                      </div>
                      <div className="kpi-val">
                        {clearedCount} / {TENOR}
                      </div>
                      <div className="kpi-sub">
                        {t('installments.monthsToGo', { count: TENOR - clearedCount })}
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      <div className="inst-mini-progress-track">
                        <div
                          className="inst-mini-progress-bar"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* KPI 2: Fixed Monthly Amount (EMI) */}
                <Card>
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <CreditCard size={14} />
                          </span>
                          {t('installments.monthly')}
                        </span>
                        <Badge tone="accent">0% Markup</Badge>
                      </div>
                      <div className="kpi-val">
                        <AmountDisplay amount={sub.data.monthlyAmount} />
                      </div>
                      <div className="kpi-sub">
                        Fixed monthly commitment for 120 consecutive installments.
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      <span className="muted" style={{ fontSize: '0.71875rem' }}>
                        10-Year Fixed Subscription Plan
                      </span>
                    </div>
                  </div>
                </Card>

                {/* KPI 3: Amount Due Now (BD-20) */}
                <Card>
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Calendar size={14} />
                          </span>
                          {t('installments.amountDueNow')}
                        </span>
                        <Badge tone={amountDueNow > 0 ? 'warning' : 'success'}>
                          {amountDueNow > 0 ? t('installments.due') : t('installments.phase_cleared')}
                        </Badge>
                      </div>
                      <div className="kpi-val">
                        <AmountDisplay amount={amountDueNow} />
                      </div>
                      <div className="kpi-sub">
                        {nextUnsettledRow ? (
                          <span>
                            {t('installments.nextMonthDue', {
                              month: nextUnsettledRow.installmentNumber,
                              date: formatDate(nextUnsettledRow.dueDate, locale),
                            })}
                          </span>
                        ) : (
                          <span>{t('installments.allCleared')}</span>
                        )}
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      {amountDueNow > 0 && !isClosed ? (
                        <PayLink
                          to={BKASH_ENABLED ? '/payments/bkash' : PAY_FALLBACK_PATH}
                          className="kpi-cta-primary"
                        >
                          <Button variant="primary" size="sm" style={{ width: '100%' }}>
                            {BKASH_ENABLED ? `⚡ ${t('payments.payWithBkash')}` : t('payments.uploadBankSlip')}
                          </Button>
                        </PayLink>
                      ) : (
                        <span className="muted" style={{ fontSize: '0.75rem' }}>
                          {isClosed ? t('installments.closedBannerTitle') : t('installments.noOverdue')}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>

                {/* KPI 4: Advance Credit Card */}
                <InstallmentGrid.AdvanceCard
                  rows={rows.data}
                  advanceBalance={sub.data.advanceBalance}
                  prepaidAmount={sub.data.prepaidAmount}
                  prepaidMonths={sub.data.prepaidMonths}
                  monthlyAmount={sub.data.monthlyAmount}
                />
              </div>

              {/* 3. Schedule Section: Pre-Verification State vs Error vs 120-Month Visual Matrix */}
              {rows.isError ? (
                <Card>
                  <QueryState
                    loading={false}
                    error={rows.error}
                    onRetry={() => void rows.refetch()}
                  />
                </Card>
              ) : isScheduleEmpty ? (
                <Card className="inst-schedule-unverified-card" data-testid="schedule-not-generated">
                  <div style={{ padding: '40px 20px', textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
                    <div
                      style={{
                        display: 'inline-flex',
                        padding: '14px',
                        borderRadius: '50%',
                        background: '#fffbeb',
                        color: '#d97706',
                        marginBottom: '14px',
                      }}
                    >
                      <Clock size={32} />
                    </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--color-text, #0f172a)' }}>
                      {t('installments.scheduleNotGeneratedTitle')}
                    </h2>
                    <p className="muted" style={{ fontSize: '0.875rem', lineHeight: 1.6, margin: '0 0 16px 0' }}>
                      {t('installments.scheduleNotGeneratedDesc')}
                    </p>
                    <Badge tone="warning">
                      {t('installments.bookingPending')}
                    </Badge>
                  </div>
                </Card>
              ) : (
                <Card className="inst-matrix-card">
                  <div className="inst-matrix-header">
                    <div className="inst-matrix-header-left">
                      <div className="inst-matrix-eyebrow">
                        <Sparkles size={14} color="#d4af37" />
                        <span>{t('installments.scheduleArchitecture')}</span>
                      </div>
                      <h2 className="inst-matrix-title">
                        {t('installments.gridHint')}
                      </h2>
                      <p className="inst-matrix-desc">
                        {t('installments.interactiveSchedule')}
                      </p>
                    </div>
                  </div>

                  <InstallmentGrid.Root>
                    <InstallmentGrid.Matrix
                      rows={rows.data}
                      advanceBalance={sub.data.advanceBalance}
                      monthlyAmount={sub.data.monthlyAmount}
                      delinquency={sub.data.delinquencyStatus}
                      onSelect={setActive}
                    />
                  </InstallmentGrid.Root>
                </Card>
              )}
            </>
          )}

          {/* 4. Filter Bar for Tabular Ledger */}
          {!isScheduleEmpty && !rows.isError && (
            <div className="inst-filter-strip">
              <div className="inst-filter-left">
                <Filter size={16} color="var(--color-primary, #0d7c3d)" />
                <label className="inst-filter-label" htmlFor="inst-filter">
                  {t('installments.status')} Filter:
                </label>
                <select
                  id="inst-filter"
                  className="inst-filter-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as InstallmentStatus | 'ALL')}
                  data-testid="inst-filter"
                >
                  {FILTERS.map((f) => {
                    const label = f === 'ALL'
                      ? t('payments.all')
                      : t(getInstallmentStatusSpec(f).labelKey);
                    return (
                      <option key={f} value={f}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <span className="muted" style={{ fontSize: '0.8125rem' }}>
                {t('installments.showingFiltered', { filtered: filtered.length, total: TENOR })}
              </span>
            </div>
          )}

          {!rows.isError && (
            <QueryState
              loading={rows.isPending}
              error={null}
              empty={filtered.length === 0 && !isScheduleEmpty}
              onRetry={() => void rows.refetch()}
              skeletonCount={6}
            />
          )}

          {/* 5. Detailed Installment Ledger Table */}
          {filtered.length > 0 && !rows.isError && (
            <Card>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>#</th>
                      <th>{t('installments.dueDate')}</th>
                      <th>{t('installments.paidDate')}</th>
                      <th>{t('installments.amount')}</th>
                      <th>{t('installments.status')}</th>
                      <th style={{ textAlign: 'right' }}>{t('installments.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const statusSpec = getInstallmentStatusSpec(r.status, r.dueDate);
                      const isPaid = r.status === InstallmentStatus.PAID;
                      const isAdvanced = r.status === InstallmentStatus.ADVANCED;
                      const isUpcoming = r.status === InstallmentStatus.UPCOMING && !statusSpec.isPayable;
                      const canPay = statusSpec.isPayable && !isClosed && amountDueNow > 0;

                      return (
                        <tr key={r.id} data-testid="inst-row">
                          <td className="tabular" style={{ fontWeight: 700 }}>
                            M-{String(r.installmentNumber).padStart(3, '0')}
                          </td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Calendar size={13} color="#64748b" />
                              {formatDate(r.dueDate, locale)}
                            </span>
                          </td>
                          <td>
                            {r.paidDate ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Clock size={13} color="#059669" />
                                {formatDate(r.paidDate, locale)}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            <AmountDisplay amount={r.amount} />
                          </td>
                          <td>
                            <Badge tone={statusSpec.tone}>
                              {t(statusSpec.labelKey)}
                            </Badge>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {isPaid ? (
                              <Link to="/payments" className="kpi-link">
                                {t('installments.viewReceipt')}
                              </Link>
                            ) : canPay ? (
                              <PayLink to={BKASH_ENABLED ? '/payments/bkash' : PAY_FALLBACK_PATH}>
                                <Button variant="secondary" size="sm">
                                  {t('installments.pay')}
                                </Button>
                              </PayLink>
                            ) : isAdvanced ? (
                              <span className="muted" style={{ fontSize: '0.8125rem' }}>
                                {t('installments.advanced')}
                              </span>
                            ) : isUpcoming ? (
                              <span className="muted" style={{ fontSize: '0.8125rem' }}>
                                {t('installments.upcoming')}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* 6. Month Detail Modal */}
      <InstallmentGrid.Modal
        slot={active}
        delinquency={sub.data?.delinquencyStatus}
        onClose={() => setActive(null)}
      />
    </div>
  );
}
